import React from 'react';
import styled from 'styled-components';
import { RiArrowUpLine, RiArrowDownLine } from '@remixicon/react';
import { colors } from '@/constants/colors';
import type { Uptime } from '@/utils/uptime';

/**
 * "How long has it been like this" — one rendering, used in four places.
 *
 * The operator's card, the operator's list, the Connection tab and the admin
 * fleet list all answer the same question, and it has to read identically in
 * all four: an operator quoting "1d 15h down" in a support message must mean
 * the same thing the admin sees. Separate JSX would drift on the first change.
 *
 * ARROW, NOT A WORD (Ray, 2026-08-16). "Offline 12d 16h" wrapped onto three
 * lines in a narrow table column and made every row a different height. The
 * arrow carries the same meaning in one character, so the whole value fits on
 * one line: ↓ red for down, ↑ green for up. The cell refuses to wrap — a column
 * that grows a second line is worse than one that scrolls.
 *
 * A box we cannot anchor renders an em dash, never "0m". Older firmware reports
 * no history, and inventing a zero there would be a confident lie about exactly
 * the boxes we know least about — which is the opposite of the point.
 */

const Wrap = styled.span<{ $down?: boolean }>`
   display: inline-flex;
   align-items: center;
   gap: 4px;
   /* Never wrap. This is the whole reason the word was dropped for an arrow. */
   white-space: nowrap;
   font-variant-numeric: tabular-nums;
   font-weight: 600;
   color: ${({ $down }) => ($down ? colors.RED : colors.SUCCESS)};
`;

const Empty = styled.span`
   color: ${colors.GRAY5};
`;

export const UptimeCell = ({ up }: { up: Uptime | null | undefined }) => {
   if (!up)
      return (
         <Empty title="This box has not reported a connection history yet">
            —
         </Empty>
      );
   const Icon = up.down ? RiArrowDownLine : RiArrowUpLine;
   return (
      <Wrap
         $down={up.down}
         title={up.title}
         aria-label={`${up.down ? 'Offline for' : 'Online for'} ${up.duration}`}
      >
         <Icon size={14} aria-hidden />
         {up.duration}
      </Wrap>
   );
};

export default UptimeCell;
